import React, { useEffect, useRef, useState } from 'react';
import { sanitizeHtml } from './RichTextField';

// UDRAW-S4 — shared Discussion component. Renders a comment list and
// an always-editing rich editor (B/I/U/UL/OL via document.execCommand
// on a contenteditable div, DOMPurify sanitize on save). Reused by
// WorkItemDrawer (WI comments) and TaskDrawerForm (task comments per
// UDRAW-S4 §2.1).
//
// Props:
//   comments  — [{ id, authorFullName, text, createdAt }]
//   onPost    — async (html) => void; the caller posts and appends the
//               server DTO to its own state. This component clears
//               the editor + toasts errors via the returned rejection.
//   testIdPrefix — 'wi-drawer' | 'task-drawer' — every testid is
//               scoped so the WI and task discussions don't clash.
//   canPost   — false hides the editor + Post button; the list still
//               renders (e.g. for readers).
//   maxLength — spec cap, default 2000 chars (after sanitize).

const ALLOWED_TAGS_HINT = ['b', 'i', 'u', 'ul', 'ol', 'li', 'p', 'br'];

const isoDate = (v) => {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('sv-SE');
};

export default function Discussion({
  comments,
  onPost,
  testIdPrefix,
  canPost = true,
  maxLength = 2000,
  // UDRAW-S5.1 §2.2.4 — the drawer fires live.startEditing /
  // stopEditing from these when the always-editing rich comment editor
  // gets/loses focus. Post ALSO fires stopEditing (the parent handles
  // it via onPost + editor blur — clearEditor doesn't blur on its
  // own). Both are optional; older parents keep working.
  onEditorFocus,
  onEditorBlur,
}) {
  const list = Array.isArray(comments) ? comments : [];
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');
  const editorRef = useRef(null);
  const listTestId = `${testIdPrefix}-comments`;
  const editorTestId = `${testIdPrefix}-new-comment-editor`;
  const postTestId = `${testIdPrefix}-post-comment`;
  const errTestId = `${testIdPrefix}-new-comment-err`;

  // Restore an empty editor after a successful post. The
  // contenteditable is uncontrolled — the DOM is the source of truth
  // for the user's typing. As long as this Discussion instance stays
  // mounted (which it does under A10's silent refresh — DeliveryPage
  // no longer swaps its subtree on live events), typing survives every
  // re-render without any explicit backing store. The `key={anchorId}`
  // that the parents put on this component makes the identity change
  // explicit only when the drawer switches to a different WI / task,
  // at which point a fresh empty editor is the correct behaviour.
  const clearEditor = () => {
    if (editorRef.current) editorRef.current.innerHTML = '';
  };

  const exec = (cmd) => (e) => {
    // Keep focus + selection inside the contenteditable — the button's
    // default mousedown steals focus otherwise, which drops the caret.
    e.preventDefault();
    document.execCommand(cmd, false, null);
  };

  const doPost = async () => {
    const rawHtml = editorRef.current?.innerHTML || '';
    const cleaned = sanitizeHtml(rawHtml);
    // Empty guard — strip tags and check the visible payload.
    const visible = cleaned.replace(/<[^>]*>/g, '').trim();
    if (!visible) {
      setError('Comment text is required.');
      return;
    }
    if (cleaned.length > maxLength) {
      setError(`Too long (${cleaned.length}/${maxLength} characters). Trim and try again.`);
      return;
    }
    if (typeof onPost !== 'function') return;
    setPosting(true);
    setError('');
    try {
      await onPost(cleaned);
      clearEditor();
    } catch (err) {
      // Read the server body (WorkItemRuleException envelope
      // `{code, message}`) before falling back to the axios top-level
      // message — otherwise a 400 TEXT_TOO_LONG surfaces as the useless
      // "Request failed with status code 400".
      const body = err?.response?.data;
      const serverMsg = (body && typeof body === 'object'
        ? (body.message || body.Message || body.detail)
        : (typeof body === 'string' ? body : null));
      setError(serverMsg || err?.message || 'Could not post the comment.');
    } finally {
      setPosting(false);
    }
  };

  // Reset any stale error when the caller hands us a new empty draft
  // (e.g. drawer reopened on another anchor).
  useEffect(() => {
    setError('');
  }, [testIdPrefix]);

  return (
    <div data-testid={`${testIdPrefix}-discussion`}>
      <div className="wi-drawer-sec-t">Discussion</div>
      <div data-testid={listTestId}>
        {list.map((c) => (
          <div
            className="comment"
            key={c.id}
            data-testid={`${testIdPrefix}-comment-${c.id}`}
            style={{
              background: '#f8f8fa', border: '1px solid #e0e0e0',
              padding: '6px 8px', borderRadius: 3, marginBottom: 6,
            }}
          >
            <div className="meta" style={{ fontSize: 11, fontWeight: 700, color: '#444' }}>
              {c.authorFullName}
              <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                · {isoDate(c.createdAt)}
              </span>
            </div>
            <div
              className="body"
              style={{ marginTop: 3, fontSize: 13, whiteSpace: 'pre-wrap' }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(c.text) }}
            />
          </div>
        ))}
        {list.length === 0 && (
          <div style={{ color: '#999', fontSize: 12 }}>No comments yet.</div>
        )}
      </div>
      {canPost && (
        <>
          <div className="udraw-rte" style={{ marginTop: 6 }} data-testid={`${testIdPrefix}-new-comment`}>
            <div className="udraw-rtb" role="toolbar">
              <button type="button" onMouseDown={exec('bold')}
                      data-testid={`${testIdPrefix}-new-comment-b`}><b>B</b></button>
              <button type="button" onMouseDown={exec('italic')}
                      data-testid={`${testIdPrefix}-new-comment-i`}><i>I</i></button>
              <button type="button" onMouseDown={exec('underline')}
                      data-testid={`${testIdPrefix}-new-comment-u`}><u>U</u></button>
              <button type="button" onMouseDown={exec('insertUnorderedList')}
                      data-testid={`${testIdPrefix}-new-comment-ul`}>• ≡</button>
              <button type="button" onMouseDown={exec('insertOrderedList')}
                      data-testid={`${testIdPrefix}-new-comment-ol`}>1. ≡</button>
            </div>
            <div
              ref={editorRef}
              className="udraw-rta"
              contentEditable
              suppressContentEditableWarning
              data-testid={editorTestId}
              aria-label={`New comment (${ALLOWED_TAGS_HINT.join('/')} formatting)`}
              onFocus={() => { if (typeof onEditorFocus === 'function') { try { onEditorFocus(); } catch { /* ignore */ } } }}
              onBlur={() => { if (typeof onEditorBlur === 'function') { try { onEditorBlur(); } catch { /* ignore */ } } }}
            />
            {error && (
              <div className="udraw-ed-err" data-testid={errTestId}>{error}</div>
            )}
            <div className="udraw-ed-actions">
              <button
                type="button"
                className="udraw-mini pri"
                data-testid={postTestId}
                onClick={doPost}
                disabled={posting}
              >{posting ? 'Posting…' : 'Post'}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
